import { create } from 'zustand';
import type { StreamStats, SubtitleCue, ChatMessage, FloatingReaction, SavedSubtitle, VideoQuality } from '@/types';

export interface BufferedProgress {
  bufferedPercent: number;
  bufferHealthSeconds: number;
  bufferedTime: number;
  isStreamingBuffer: boolean;
}

interface PlayerStore {
  // Playback & Media Source
  mediaType: 'local' | 'youtube';
  youtubeVideoId: string | null;
  guestStreamSrc: string | null;
  bufferedProgress: BufferedProgress;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  muted: boolean;
  playbackRate: number;
  isBuffering: boolean;
  autoplayBlocked: boolean;
  isFullscreen: boolean;

  // Quality
  stats: StreamStats | null;
  selectedQuality: VideoQuality;

  // Subtitles
  subtitleCues: SubtitleCue[];
  activeSubtitle: string | null;
  subtitleEnabled: boolean;
  subtitleOffset: number; // seconds (+ or - offset)
  savedSubtitles: SavedSubtitle[];
  activeSubtitleId: string | null;

  // Chat & Reactions
  chatMessages: ChatMessage[];
  unreadChatCount: number;
  reactions: FloatingReaction[];

  // Sync lock — prevents feedback loops
  syncLockUntil: number;

  // Actions
  setMediaType: (mediaType: 'local' | 'youtube') => void;
  setYouTubeVideoId: (id: string | null) => void;
  setGuestStreamSrc: (src: string | null) => void;
  setBufferedProgress: (progress: Partial<BufferedProgress>) => void;
  setPlaying: (v: boolean) => void;
  setCurrentTime: (v: number) => void;
  setDuration: (v: number) => void;
  setVolume: (v: number) => void;
  setMuted: (v: boolean) => void;
  setPlaybackRate: (v: number) => void;
  setBuffering: (v: boolean) => void;
  setAutoplayBlocked: (v: boolean) => void;
  setFullscreen: (v: boolean) => void;
  setStats: (s: StreamStats | null) => void;
  setSelectedQuality: (q: VideoQuality) => void;
  setSubtitleCues: (cues: SubtitleCue[]) => void;
  setActiveSubtitle: (text: string | null) => void;
  setSubtitleEnabled: (v: boolean) => void;
  setSubtitleOffset: (offset: number) => void;
  addSavedSubtitle: (sub: SavedSubtitle) => void;
  selectSavedSubtitle: (id: string | null) => void;
  removeSavedSubtitle: (id: string) => void;
  addChatMessage: (msg: ChatMessage) => void;
  setChatMessages: (msgs: ChatMessage[]) => void;
  incrementUnreadChat: () => void;
  clearUnreadChat: () => void;
  addReaction: (reaction: FloatingReaction) => void;
  removeReaction: (id: string) => void;
  lockSync: (durationMs: number) => void;
  isSyncLocked: () => boolean;
}

export const usePlayerStore = create<PlayerStore>((set, get) => ({
  mediaType: 'local',
  youtubeVideoId: null,
  guestStreamSrc: null,
  bufferedProgress: {
    bufferedPercent: 0,
    bufferHealthSeconds: 0,
    bufferedTime: 0,
    isStreamingBuffer: false,
  },
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  volume: 1,
  muted: false,
  playbackRate: 1,
  isBuffering: false,
  autoplayBlocked: false,
  isFullscreen: false,
  stats: null,
  selectedQuality: '1080p',
  subtitleCues: [],
  activeSubtitle: null,
  subtitleEnabled: true,
  subtitleOffset: 0,
  savedSubtitles: [],
  activeSubtitleId: null,
  chatMessages: [],
  unreadChatCount: 0,
  reactions: [],
  syncLockUntil: 0,

  setMediaType: (mediaType) => set({ mediaType }),
  setYouTubeVideoId: (youtubeVideoId) => set({ youtubeVideoId }),
  setGuestStreamSrc: (guestStreamSrc) => set({ guestStreamSrc }),
  setBufferedProgress: (progress) =>
    set((state) => ({
      bufferedProgress: { ...state.bufferedProgress, ...progress },
    })),
  setPlaying: (v) => set({ isPlaying: v }),
  setCurrentTime: (v) => set({ currentTime: v }),
  setDuration: (v) => set({ duration: v }),
  setVolume: (v) => set({ volume: v }),
  setMuted: (v) => set({ muted: v }),
  setPlaybackRate: (v) => set({ playbackRate: v }),
  setBuffering: (v) => set({ isBuffering: v }),
  setAutoplayBlocked: (v) => set({ autoplayBlocked: v }),
  setFullscreen: (v) => set({ isFullscreen: v }),
  setStats: (s) => set({ stats: s }),
  setSelectedQuality: (q) => set({ selectedQuality: q }),
  setSubtitleCues: (cues) => set({ subtitleCues: cues }),
  setActiveSubtitle: (text) => set({ activeSubtitle: text }),
  setSubtitleEnabled: (v) => set({ subtitleEnabled: v }),
  setSubtitleOffset: (offset) => set({ subtitleOffset: offset }),

  addSavedSubtitle: (sub) =>
    set((state) => {
      const filtered = state.savedSubtitles.filter((s) => s.id !== sub.id);
      return {
        savedSubtitles: [...filtered, sub],
        activeSubtitleId: sub.id,
        subtitleCues: sub.cues,
        subtitleEnabled: true,
      };
    }),

  selectSavedSubtitle: (id) =>
    set((state) => {
      if (!id) {
        return { activeSubtitleId: null, subtitleCues: [], activeSubtitle: null };
      }
      const target = state.savedSubtitles.find((s) => s.id === id);
      if (!target) return {};
      return {
        activeSubtitleId: id,
        subtitleCues: target.cues,
        subtitleEnabled: true,
      };
    }),

  removeSavedSubtitle: (id) =>
    set((state) => ({
      savedSubtitles: state.savedSubtitles.filter((s) => s.id !== id),
      ...(state.activeSubtitleId === id
        ? { activeSubtitleId: null, subtitleCues: [], activeSubtitle: null }
        : {}),
    })),

  addChatMessage: (msg) =>
    set((state) => ({
      chatMessages: [...state.chatMessages.slice(-99), msg],
    })),
  setChatMessages: (msgs) => set({ chatMessages: msgs }),
  incrementUnreadChat: () =>
    set((state) => ({ unreadChatCount: state.unreadChatCount + 1 })),
  clearUnreadChat: () => set({ unreadChatCount: 0 }),

  addReaction: (reaction) =>
    set((state) => ({
      reactions: [...state.reactions.slice(-15), reaction],
    })),
  removeReaction: (id) =>
    set((state) => ({
      reactions: state.reactions.filter((r) => r.id !== id),
    })),

  lockSync: (durationMs) => set({ syncLockUntil: Date.now() + durationMs }),
  isSyncLocked: () => Date.now() < get().syncLockUntil,
}));
